<script lang="ts">
  /**
   * PREMIUM TIDE — SPY net call/put premium, spot, and net options volume (plan §13, SPX-009).
   *
   * SVG, not canvas: an SVG inherits CSS, so a theme flip repaints every mark for free. Same
   * reasoning as the dealer rail; the chart engine needs its canvas bridge precisely because canvas
   * cannot do this.
   *
   * FIVE DECISIONS ARE RULED OR MEASURED, NOT PREFERENCES:
   *
   * 1. STACKED PANES, NOT AN OVERLAY — and this is a deliberate deviation from the ratified mock.
   *    The desk ruled spot IN (cb5c649f) because Scott's divergence read is the panel's point. The
   *    mock draws spot over the premium curves on a SECOND, hidden scale (`Ys` vs `Yt`) — which is
   *    two axes on one chart, and law 10 says one. Premium is in hundreds of millions of dollars and
   *    spot is ~768; sharing a scale is not possible and a hidden second scale lets a reader compare
   *    two lines that were never comparable. So the panes STACK on one shared time axis: each chart
   *    keeps a single axis, and the divergence read survives intact because it was always a read
   *    about TIME alignment, never about the lines touching.
   *
   * 2. NO CONTROLS. The mock carries ALL/INDEX/ETF · 0DTE/W · ALL/ITM/OTM/ATM. The payload has no
   *    expiry and no moneyness dimension, so not one of them could filter anything. Ruled OMIT under
   *    D-3 (cb5c649f): a control that changes the label and nothing else teaches a false belief.
   *    The missing dimensions are named on the panel instead, so the absence is legible.
   *
   * 3. AGE IS THE DATA'S, NOT THE POLL'S. Both are shown and they are labelled differently, because
   *    on this feed they differ by hours: measured 08-07, the newest tide point was 14.6h old while
   *    the poll was 30s old (ledger D-6). A single "31s" chip, as the mock has, would be a lie in the
   *    fresh direction — the exact failure class this repo exists to refuse.
   *
   * 4. SESSION-AWARE AS OF 2026-08-07 21:4xZ — and the condition that gated it is DISCHARGED, not
   *    waived. This block used to read "NOT SESSION-AWARE, DELIBERATELY … until SPX-010's
   *    per-instrument verdict serves SPY". That verdict deployed at 21:34Z and this panel now
   *    consumes it, so the old text became a comment asserting the opposite of the code — the defect
   *    this repo names as worse than no comment at all. `session.ts` is STILL ES-ONLY and is STILL
   *    not used here; the verdicts come from the SERVER, per instrument, which is R-3's whole point.
   *
   *    TWO INSTRUMENTS, TWO SESSIONS, RENDERED INDEPENDENTLY — never one inherited verdict:
   *        MARKET (tide)  09:30 → 16:10 ET      SPY (spot overlay)  09:30 → 20:00 ET
   *    For ~3h50m every weekday one is legitimately CLOSED while the other is legitimately LIVE.
   *
   * 5. EVERY AGE AND VERDICT ON SCREEN NAMES THE SERIES IT DESCRIBES. The age chip used to read a
   *    bare "data 1h 27m" while the panel drew TWO series on different clocks — one value spanning
   *    two things with different properties, which is the defect class this fleet hit five separate
   *    times on 08-07 (one budget/two cadences · one threshold/two lanes · one breach counter/two
   *    clocks · one `budget_s`/two sources · one `src`/two boundaries). This panel was the sixth.
   *    `dataAge` is derived from the TIDE series' own coverage, so it is labelled with that series'
   *    symbol and cannot be read as a claim about the overlay below it.
   *
   * Everything derived here is HYPOTHESIS until the tide STUDY rules (law 6).
   */
  import { onMount } from "svelte";
  import {
    buildTide,
    alignSpot,
    parseSpot,
    dataAgeS,
    dataAgeState,
    formatTideMinute,
    sharedTideCursor,
    tideBucketAt,
    tideMarketSessionDomain,
    tideTimeAtX,
    tideTimeX,
    type TideSeries,
    type SpotPoint,
  } from "./tide";
  import { fetchTide, formatAge, type TideResponse } from "./api";
  import PanelFreshness from "./PanelFreshness.svelte";
  import PanelInfo from "./PanelInfo.svelte";
  import AgePill from "./AgePill.svelte";
  import { reportDoorFailure, servedClosure, type DoorFaultEvidence } from "./faultEvidence";

  interface Props {
    /** Plan Q2: the tide/flow budget is 3 minutes. */
    budgetS?: number;
    pollMs?: number;
  }
  let { budgetS = 180, pollMs = 30_000 }: Props = $props();

  let feed = $state<TideResponse | null>(null);
  let feedFetchedAtMs = $state<number | null>(null);
  // /api/tide serves its receipt age as poll_age_s (tide.ts pollAgeS(receiptTs, now)), not age_s.
  let agePayload = $derived(feed === null ? null : { age_s: feed.poll_age_s, budget_s: feed.budget_s, root: feed.root });
  let fault = $state<DoorFaultEvidence | null>(null);
  let loading = $state(true);
  let nowMs = $state(Date.now());
  let cursorTMs = $state<number | null>(null);
  let infoOpen = $state(false);
  /**
   * ⚠ ORDER 42 (DESK-n38 R1103, owner word 2026-08-31) — THE CROSSOVER MARKS ARE RETIRED.
   * The purple diamonds, their HH:MMZ stamps, the toggle chip and the counter that reported
   * them are gone from this panel. `tide.ts`'s `tideCrosses` is untouched
   * (it is outside this order's fence and still under the tide STUDY), but NOTHING DRAWS IT here.
   * The ids are DECLARED lost below (`data-retired-series-ids`) — the `parseStoredLayout` idiom:
   * a loss that is NAMED is a decision, a loss that is silent is indistinguishable from a break.
   * The NCP/NPP curves, the spot pane, the volume pane and the staleness chips are UNAFFECTED.
   */
  const RETIRED_SERIES_IDS = [
    "tideCross:diamond",
    "tideCross:stamp",
    "tideCross:chip",
  ];

  let series = $derived<TideSeries | null>(feed ? buildTide(feed.tide) : null);
  let spotPts = $derived<SpotPoint[] | null>(feed?.spot ? parseSpot(feed.spot) : null);
  let spotAligned = $derived<(number | null)[]>(
    series && spotPts ? alignSpot(series.points, spotPts) : [],
  );
  /** Spot is present only if the join actually produced values — a series of nulls is still absent. */
  let spotUsable = $derived(spotAligned.some((v) => v !== null));
  let dataAge = $derived(series ? dataAgeS(series, nowMs) : null);
  let ageState = $derived(series ? dataAgeState(series, nowMs) : null);
  let sessionDomain = $derived(
    series?.points[0] && series.sessionDate
      ? tideMarketSessionDomain(series.points[0].tMs, series.sessionDate)
      : null,
  );

  /**
   * CADENCE-AWARE STALENESS — because the flat 180s budget fires on a HEALTHY feed 40% of the time.
   *
   * Measured 08-07, n=120 over 4h of live session, real rows:
   *     bar cadence (distinct newest points) : 300s  — the tide is a FIVE-MINUTE series
   *     poll cadence (our receipts)          : 120s
   *     data age                             : min 12s · p50 132s · p90 252s · max 252s
   *     >>> over plan Q2's 180s budget       : 48/120 = 40.0%, with NOTHING BROKEN
   *
   * This is ledger B-1 one level over. There, the 90s PRICE budget was found unachievable because
   * upstream arrival spacing alone exceeded it (141s peak, 1.16% breach). Here the same arithmetic
   * is far more severe: a 5-minute series simply cannot be fresher than its own bar cadence, so age
   * sawtooths 0→~420s (bar + poll) by construction. A chip that reddens on 40% of healthy samples is
   * the alarm-fatigue failure this repo has spent the whole day removing — it would be ignored inside
   * a week, and then worthless on the day the feed actually dies.
   *
   * THE CAPSULE'S NUMBER IS NOT CHANGED HERE. Plan Q2 sets "tide/flow ≤3min" and Q2 is capsule
   * authority; B-1's precedent is that CC records measured evidence and ROUTES it rather than
   * editing the plan. So `budgetS` keeps its ruled value and is still what the panel REPORTS
   * against; what is cadence-derived is only the threshold at which the chip goes LOUD. Routed to
   * the desk with the measurement — if they re-size Q2, delete this and use the number.
   *
   * The floor is deliberately `budgetS` itself: this can only ever make the chip QUIETER than a
   * structurally-impossible budget, never more forgiving than a reachable one.
   */
  const BAR_CADENCE_S = 300;
  const POLL_CADENCE_S = 120;
  /**
   * TRANSPORT MARGIN — added per the desk's amendment (3c02c242): budgets derive from native cadence
   * PLUS measured transport, never a global constant. BUILDER-a measured the copy lag at 39s observed
   * against a 50s ceiling (ledger V-5, n=40); the CEILING is used, because a budget sized on an
   * observed max is the n=12 mistake this fleet has already paid for twice.
   *   300 (5m bars) + 120 (poll) + 50 (transport ceiling) = 470s
   */
  const TRANSPORT_CEILING_S = 50;
  let loudThresholdS = $derived(
    Math.max(budgetS, BAR_CADENCE_S + POLL_CADENCE_S + TRANSPORT_CEILING_S),
  );
  let overBudget = $derived(dataAge !== null && dataAge > loudThresholdS);

  /**
   * ⇒ THE SERVER'S PER-INSTRUMENT VERDICT NOW SUPERSEDES EVERYTHING ABOVE (SPX-010, live since the
   * 21:34Z deploy). R-3 ruled the verdict belongs server-side per instrument; the cadence-derived
   * threshold above was this panel's stand-in while no verdict existed, and it is now the FALLBACK
   * for a rolled-back Worker, not the primary. The UI does not compute a more forgiving definition
   * of fresh than the server's — and no longer computes a competing one either.
   */
  let marketReport = $derived(feed?.series?.find((s) => s.symbol === feed?.root) ?? null);
  let spotReport = $derived(feed?.series?.find((s) => s.symbol === "SPY") ?? null);
  let venueClosed = $derived(servedClosure(marketReport?.venue_state));

  /**
   * LOUD KEYS ON THE VERDICT, NEVER ON `stale`. Measured live at 21:37Z, MARKET serves
   * `session_verdict: CLOSED` and `stale: true` SIMULTANEOUSLY — and that pair is correct: the data
   * IS older than its budget, and the reason IS that the market shut at 16:10 ET. Reddening on
   * `stale` would put this panel amber every night on a healthy closed feed, which is the precise
   * defect that kept it unmounted for a day. CLOSED is a fact about the world, not a fault.
   */
  let tideLoud = $derived(
    ageState?.state === "FUTURE_DATED"
      || (marketReport ? marketReport.session_verdict === "STALE" : overBudget),
  );

  let freshnessTitle = $derived(
    ageState?.state === "FUTURE_DATED"
      ? `tide FUTURE-DATED +${ageState.aheadS}s - clock ahead of now`
      : marketReport
      ? `${marketReport.symbol} ${marketReport.session_verdict ?? "NO VERDICT"} · ${marketReport.session_reason ?? "no reason served"}`
      : `tide age ${dataAge === null ? "unserved" : formatAge(dataAge)} · budget ${formatAge(budgetS)}`,
  );

  /**
   * THE TWO INSTRUMENTS DIVERGE FOR ~3h50m EVERY WEEKDAY and this panel draws both: MARKET closes
   * 16:10 ET, SPY runs to 20:00. `alignSpot` emits one value per TIDE point, so once the tide series
   * ends the overlay CANNOT advance — it freezes at 16:10 while SPY is still printing live.
   *
   * Nothing is wrong when that happens, but a line that STOPPED and a line that is STILL GOING look
   * identical, and under a quiet CLOSED chip a reader would reasonably conclude SPY stopped printing.
   * That is a silent staleness on the second series — the sibling of the case desk ruling cb5c649f
   * forbids ("never draw one series silently"), in wording that ruling does not reach. So it is said
   * out loud. Detected from the two SERVER verdicts, not from a clock this panel keeps itself.
   */
  let spotOutrunsTide = $derived(
    marketReport?.session_verdict === "CLOSED" && spotReport?.session_verdict === "LIVE",
  );

  async function load() {
    try {
      feed = await fetchTide();
      feedFetchedAtMs = Date.now();
      fault = null;
    } catch (err) {
      nowMs = Date.now();
      fault = reportDoorFailure("tide", err, series?.coverage?.endMs ?? null, nowMs);
    } finally {
      loading = false;
      nowMs = Date.now();
    }
  }

  onMount(() => {
    const stopCursor = sharedTideCursor.subscribe((tMs) => (cursorTMs = tMs));
    load();
    const measure = () => (compact = window.innerWidth <= 700);
    measure();
    window.addEventListener("resize", measure);
    const t = setInterval(load, pollMs);
    const tick = setInterval(() => (nowMs = Date.now()), 1000);
    return () => {
      clearInterval(t);
      clearInterval(tick);
      window.removeEventListener("resize", measure);
      stopCursor();
    };
  });

  // --- geometry: three panes, ONE shared time axis ---------------------------------
  const W = 560;
  const PAD_L = 46;
  /**
   * A RESERVED GUTTER for the direct labels, not decorative padding. Screenshots showed the NPP
   * label landing on top of the NPP curve at the right edge — the series and its own label were
   * competing for the same pixels. Direct labelling is the dataviz rule; giving the labels their
   * own column is what makes it legible instead of merely present.
   */
  /**
   * RESPONSIVE, because the mobile fix created its own defect. Scaling SVG type up at 700px made the
   * labels readable and then CLIPPED them — "NPP −154." instead of "−154.81M". A truncated number is
   * worse than an absent one: it is silently wrong rather than visibly missing. The gutter is
   * geometry, so a CSS media query could not reach it; it has to widen with the type.
   */
  let compact = $state(false);
  let PAD_R = $derived(compact ? 132 : 94);
  const PREM_T = 20, PREM_H = 108;
  const SPOT_T = 146, SPOT_H = 44;
  const VOL_T = 206, VOL_H = 40;
  const H = 262;

  let n = $derived(series?.points.length ?? 0);
  let xOf = $derived((i: number) => {
    if (!sessionDomain || !series?.points[i]) return PAD_L;
    return tideTimeX(
      series.points[i].tMs,
      sessionDomain.startMs,
      sessionDomain.endMs,
      PAD_L,
      W - PAD_L - PAD_R,
    );
  });

  function scale(vals: number[], top: number, height: number) {
    const lo = Math.min(...vals), hi = Math.max(...vals);
    const span = hi - lo || 1;
    return { lo, hi, y: (v: number) => top + height - ((v - lo) / span) * height };
  }

  let premScale = $derived(
    n ? scale(series!.points.flatMap((p) => [p.ncp, p.npp]), PREM_T, PREM_H) : null,
  );
  let spotScale = $derived.by(() => {
    if (!spotUsable || !spotPts || !sessionDomain) return null;
    const values = spotPts
      .filter((point) => point.tMs >= sessionDomain.startMs && point.tMs <= sessionDomain.endMs)
      .map((point) => point.close);
    return scale(values.length ? values : spotAligned.filter((v): v is number => v !== null), SPOT_T, SPOT_H);
  });
  let volMax = $derived(n ? Math.max(...series!.points.map((p) => Math.abs(p.netVolume))) || 1 : 1);

  const path = (pts: { x: number; y: number }[]) =>
    pts.map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  let ncpPath = $derived(
    n && premScale ? path(series!.points.map((p, i) => ({ x: xOf(i), y: premScale!.y(p.ncp) }))) : "",
  );
  let nppPath = $derived(
    n && premScale ? path(series!.points.map((p, i) => ({ x: xOf(i), y: premScale!.y(p.npp) }))) : "",
  );
  /** Split on nulls so a gap in spot renders as a GAP, never as a line bridging absent data. */
  let spotSegments = $derived.by<string[]>(() => {
    if (!spotScale) return [];
    const segs: string[] = [];
    let run: { x: number; y: number }[] = [];
    spotAligned.forEach((v, i) => {
      if (v === null) {
        if (run.length > 1) segs.push(path(run));
        run = [];
      } else run.push({ x: xOf(i), y: spotScale!.y(v) });
    });
    if (run.length > 1) segs.push(path(run));
    return segs;
  });

  /**
   * THE SCRUB — and it is load-bearing, not polish.
   *
   * The desk ratified stacking over the mock's overlay (law 10) specifically because the divergence
   * read is about TIME alignment. A layout that claims time alignment while offering two axis ticks
   * across 6h40m does not let anyone READ that alignment — so until this exists, the ratified layout
   * carries an unproven premise. The desk ordered it first for exactly that reason.
   *
   * Timestamp-based, not pixel-shared: pointer geometry publishes one UTC instant and every tide
   * surface resolves its last value at or before that instant. A sparse 5m pane therefore never
   * borrows a future value from the hovered 1m expiry pane.
   */
  let cursor = $derived(
    cursorTMs === null || !series || !sessionDomain
      || cursorTMs < sessionDomain.startMs || cursorTMs > sessionDomain.endMs
      ? null
      : tideBucketAt(series.points, cursorTMs, 300_000),
  );

  let cursorX = $derived.by<number | null>(() => {
    if (cursorTMs === null || !sessionDomain || n === 0) return null;
    if (cursorTMs < sessionDomain.startMs || cursorTMs > sessionDomain.endMs) return null;
    return tideTimeX(
      cursorTMs,
      sessionDomain.startMs,
      sessionDomain.endMs,
      PAD_L,
      W - PAD_L - PAD_R,
    );
  });

  function xToTime(clientX: number, el: SVGSVGElement): number | null {
    if (!sessionDomain || n < 2) return null;
    const r = el.getBoundingClientRect();
    if (r.width <= 0) return null;
    const vx = ((clientX - r.left) / r.width) * W; // CSS px → viewBox units
    const frac = (vx - PAD_L) / (W - PAD_L - PAD_R);
    if (frac < -0.02 || frac > 1.02) return null;
    const tMs = tideTimeAtX(
      vx,
      sessionDomain.startMs,
      sessionDomain.endMs,
      PAD_L,
      W - PAD_L - PAD_R,
    );
    return Math.round(tMs / 60_000) * 60_000;
  }

  function onMove(e: PointerEvent) {
    sharedTideCursor.set(xToTime(e.clientX, e.currentTarget as SVGSVGElement));
  }

  /** Keyboard parity: a readout reachable only by pointer is unreachable on touch and by a11y. */
  function onKey(e: KeyboardEvent) {
    if (n === 0) return;
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      const step = e.key === "ArrowLeft" ? -1 : 1;
      const current = cursor === null && cursorTMs !== null ? -1 : cursor ?? n - 1;
      const index = current < 0 ? 0 : Math.max(0, Math.min(n - 1, current + step));
      sharedTideCursor.set(series?.points[index].tMs ?? null);
    } else if (e.key === "Home") { e.preventDefault(); sharedTideCursor.set(series?.points[0].tMs ?? null); }
    else if (e.key === "End") { e.preventDefault(); sharedTideCursor.set(series?.points[n - 1].tMs ?? null); }
    else if (e.key === "Escape") { sharedTideCursor.set(null); }
  }

  let at = $derived(cursor !== null && series ? series.points[cursor] : null);
  let atSpot = $derived(
    cursor !== null ? spotAligned[cursor] ?? null : null,
  );

  /**
   * Intermediate hour ticks. Two labels over a 6h40m axis made every timestamp a guess; these are
   * placed on real hour boundaries in the data rather than at even pixel fractions, so a tick always
   * names a time that exists in the series.
   */
  let hourTicks = $derived.by<{ i: number; label: string }[]>(() => {
    if (!series || n < 3) return [];
    const out: { i: number; label: string }[] = [];
    let lastHour = -1;
    /**
     * Suppress any tick that would collide with the coverage-start or coverage-end label. The first
     * render put "14:00Z" on top of "13:30Z" — an axis whose own labels overprint each other is
     * worse than the two ticks it replaced. ~34 viewBox units is the measured width of an `hh:mmZ`
     * label at this font size; the guard is a half-width either side plus a little air.
     */
    const CLEAR = 34;
    series.points.forEach((p, i) => {
      const d = new Date(p.tMs);
      if (d.getUTCHours() === lastHour || d.getUTCMinutes() !== 0) return;
      const x = xOf(i);
      if (x - PAD_L < CLEAR || W - PAD_R - x < CLEAR) return;
      out.push({ i, label: hhmm(p.tMs) });
      lastHour = d.getUTCHours();
    });
    return out;
  });

  const money = (v: number) => {
    const a = Math.abs(v);
    const s = a >= 1e9 ? `${(v / 1e9).toFixed(2)}B` : a >= 1e6 ? `${(v / 1e6).toFixed(2)}M` : `${(v / 1e3).toFixed(0)}K`;
    return v > 0 ? `+${s}` : s;
  };
  /**
   * 24-hour UTC. `hour12` MUST be explicit: without it the runtime picks by locale, and on this
   * machine it rendered "01:30 PMZ" — a 12-hour clock with a Z welded on, which is not a time in any
   * timezone. A trader reading 01:30 for a 13:30Z session open is the kind of quiet wrongness a
   * screenshot catches and a unit test does not.
   */
  const hhmm = formatTideMinute;
</script>

<!-- `tide` is this panel's ID, matching the panel-id vocabulary `workspace.ts` already uses for its
     `collapsed` list (`news`, `flow`). The shell's layout targets it by that name rather than by
     position, so adding the news rail beside it later is a registration, not a layout rewrite. -->
<section class="card tide" data-retired-series-ids={JSON.stringify(RETIRED_SERIES_IDS)}>
  <div class="hd">
    <h2>PREMIUM TIDE</h2>
    <span class="pill">{feed?.root ?? "—"}</span>
    <span class="pill">5m</span>
    {#if series?.callTilt !== null && series?.callTilt !== undefined}
      <span class="pill">CALL TILT {series.callTilt >= 0 ? "+" : ""}{series.callTilt.toFixed(3)}</span>
      <span class="hyp">HYP</span>
    {/if}
    <!-- PanelFreshness delegates CLOSED rendering to the shared VenueClosedBanner. -->
    <PanelFreshness label="tide" venueState={ageState?.state === "FUTURE_DATED" ? null : venueClosed} stale={tideLoud} ageTitle={freshnessTitle} />
    <AgePill payload={agePayload} fetchedAtMs={feedFetchedAtMs} door="/api/tide" />
    <PanelInfo id="premium-tide-info" label="Premium tide information" open={infoOpen} ontoggle={() => (infoOpen = !infoOpen)}>
      <p>{feed?.root ?? "—"} root · 5m session · detection only</p>
      <p><span class="hyp">HYP</span> HYPOTHESIS · DISPLAY ONLY</p>
      {#if spotOutrunsTide}
        <p>The tide's session has ended; the SPY overlay stops with it. {marketReport?.symbol} {marketReport?.session_verdict} · SPY {spotReport?.session_verdict}.</p>
      {/if}
      {#if series && n === 1 && series.latest}
        <p>SESSION JUST OPENED — one point so far, too few to draw a series · session {series.sessionDate}</p>
      {/if}
      {#if n > 1}
        <p>hover or focus the chart and use ← → to read any point · Esc clears</p>
        {#if at}<p>{cursorTMs === null ? hhmm(at.tMs) : hhmm(cursorTMs)} · NCP {money(at.ncp)} · NPP {money(at.npp)} · {atSpot !== null ? `SPY ${atSpot.toFixed(2)}` : "SPY —"} · vol {at.netVolume.toLocaleString()}</p>{/if}
      {/if}
      {#if series?.callTilt !== null && series?.callTilt !== undefined}<p>call tilt {series.callTilt >= 0 ? "+" : ""}{series.callTilt.toFixed(3)}</p>{/if}
      <p>{n} points</p>
      <p>feed carries no expiry or moneyness dimension — no 0DTE/W or ITM/OTM/ATM split is possible</p>
      <p>NO PRIOR BAR · values unavailable at a cursor before the first served point</p>
      <p>OUTSIDE THIS SESSION · values unavailable</p>
    </PanelInfo>
  </div>

  <div class="body">
    {#if fault}
      <div class="loud" role="alert">
        <b>DOOR FAILURE — this panel is not showing live data</b>
        <span>{fault.label}</span>
        {#if fault.retained}<span>{fault.retained}</span>{/if}
        <button onclick={load}>Retry</button>
      </div>
    {:else if loading}
      <p class="loading" aria-label="loading premium tide">●</p>
    {/if}

    <!-- Desk condition 2: a missing overlay degrades LOUDLY. Never draw one series silently. -->
    {#if series && n > 0 && !spotUsable && !venueClosed}
      <div class="degrade" role="status">
        <b>TIDE ONLY — the SPY spot overlay is not being drawn.</b>
        <span>{feed?.spot_absent_reason ?? "no spot series in this response"} · divergence cannot be read without it</span>
      </div>
    {/if}

    <!--
      SESSION-OPEN STATE — a real live condition, not a hypothetical. The vendor's tide series
      restarts each session, so for the first ~5 minutes after the 13:30Z open the payload contains
      ONE point (measured live 08-07 13:30-13:35Z). With n=1 every path is a single moveto, both
      axis labels read the same time, and the panel would render a convincingly empty chart over
      data that genuinely exists. Drawing "nothing" from "something" is the fail-soft this repo
      refuses — so the single reading is shown as a NUMBER instead, and the reason is stated.
      Not amber: this is the market working normally, not a fault.
    -->
    {#if series && n > 1 && premScale}
      <div class="chartwrap" class:dimmed={fault !== null}>
        <!--
          role="slider" is not a workaround for the linter — it is what this control IS. The scrub
          cursor is a position along a range and the arrow keys move it, which is a slider exactly.
          It also gives screen readers the thing role="img" could never give them: a CURRENT VALUE
          that updates as you move, via aria-valuetext. An image with a tabindex would have been the
          lie; this is the honest markup.
        -->
        <svg width="100%" viewBox="0 0 {W} {H}" role="slider" tabindex="0"
             aria-label="Premium tide scrubber — market-wide net premium, with SPY spot overlay. Read any point in the session."
             aria-valuemin={0} aria-valuemax={Math.max(0, n - 1)} aria-valuenow={cursorTMs === null ? n - 1 : cursor ?? 0}
             aria-valuetext={at
               ? `${cursorTMs === null ? hhmm(at.tMs) : hhmm(cursorTMs)} crosshair${cursorTMs !== null && cursorTMs !== at.tMs ? `; values as of ${hhmm(at.tMs)}` : ""}: net call premium ${money(at.ncp)}, net put premium ${money(at.npp)}, net options volume ${at.netVolume.toLocaleString()}${atSpot !== null ? `, SPY spot ${atSpot.toFixed(2)}` : ", SPY spot not available"}`
               : cursorTMs !== null && cursorX !== null
                 ? `${hhmm(cursorTMs)} crosshair; ABSENT; net call premium unavailable, net put premium unavailable, net options volume unavailable, SPY spot ${atSpot === null ? "unavailable" : atSpot.toFixed(2)}`
               : cursorTMs !== null
                 ? `${hhmm(cursorTMs)} crosshair outside this session; values unavailable`
               : `${n} points, ${hhmm(series.coverage!.startMs)} to ${hhmm(series.coverage!.endMs)}. Arrow keys to read individual points.`}
             class:scrubbing={cursor !== null}
             onpointermove={onMove}
             onpointerdown={onMove}
             onpointerleave={() => sharedTideCursor.set(null)}
             onkeydown={onKey}>
          <!-- PANE 1 · premium. One axis. -->
          <text x={PAD_L} y={PREM_T - 2} class="paneln">NET PREMIUM · {feed?.root ?? "—"}</text>
          <text x="4" y={PREM_T + 8} class="axl">{money(premScale.hi)}</text>
          <text x="4" y={PREM_T + PREM_H} class="axl">{money(premScale.lo)}</text>
          <line x1={PAD_L} x2={W - PAD_R} y1={premScale.y(0)} y2={premScale.y(0)} class="zero" />
          <path d={ncpPath} class="ncp" />
          <path d={nppPath} class="npp" />
          <!-- Direct labels: identity is never colour-alone. -->
          <text x={W - PAD_R + 6} y={premScale.y(series.latest!.ncp) + 3} class="lbl ncpl">NCP {money(series.latest!.ncp)}</text>
          <text x={W - PAD_R + 6} y={premScale.y(series.latest!.npp) + 3} class="lbl nppl">NPP {money(series.latest!.npp)}</text>

          <!-- PANE 2 · spot, its own axis. Absent ⇒ the pane says so rather than vanishing. -->
          <text x="4" y={SPOT_T + 8} class="axl">{spotScale ? spotScale.hi.toFixed(2) : ""}</text>
          <text x="4" y={SPOT_T + SPOT_H} class="axl">{spotScale ? spotScale.lo.toFixed(2) : ""}</text>
          {#if spotScale}
            {#each spotSegments as d}<path {d} class="spot" />{/each}
            <text x={PAD_L} y={SPOT_T - 3} class="paneln">SPY SPOT</text>
          {:else}
            <text x={PAD_L} y={SPOT_T + SPOT_H / 2} class="paneln absent">SPY SPOT — NOT DRAWN (no aligned series)</text>
          {/if}

          <!-- PANE 3 · net options volume, its own axis, zero rule drawn. -->
          <text x={PAD_L} y={VOL_T - 3} class="paneln">NET OPTIONS VOLUME</text>
          <line x1={PAD_L} x2={W - PAD_R} y1={VOL_T + VOL_H / 2} y2={VOL_T + VOL_H / 2} class="zero" />
          {#each series.points as p, i (p.tMs)}
            {@const h = (Math.abs(p.netVolume) / volMax) * (VOL_H / 2)}
            <rect class="vol {p.netVolume >= 0 ? 'pos' : 'neg'}"
                  x={xOf(i) - 1.6} width="3.2" rx="1"
                  y={p.netVolume >= 0 ? VOL_T + VOL_H / 2 - h : VOL_T + VOL_H / 2}
                  height={Math.max(1, h)}><title>{hhmm(p.tMs)} · net vol {p.netVolume.toLocaleString()}</title></rect>
          {/each}

          <!-- THE SCRUB RULE — spans all three panes, which is the whole point: one vertical, one
               instant, read across premium, spot and volume simultaneously. -->
          {#if cursorX !== null}
            <line x1={cursorX} x2={cursorX} y1={PREM_T} y2={VOL_T + VOL_H} class="scrub" />
            <g class="cursor-time-chip" pointer-events="none">
              <rect x={Math.max(PAD_L, Math.min(cursorX - 19, W - PAD_R - 38))} y={PREM_T + 2} width="38" height="13" rx="2" fill="var(--surface)" />
              <text x={Math.max(PAD_L, Math.min(cursorX - 19, W - PAD_R - 38)) + 19} y={PREM_T + 12} text-anchor="middle">{formatTideMinute(cursorTMs!)}</text>
            </g>
            {#if at}
              <circle cx={cursorX} cy={premScale.y(at.ncp)} r="2.6" class="dot ncpd" />
              <circle cx={cursorX} cy={premScale.y(at.npp)} r="2.6" class="dot nppd" />
              {#if spotScale && atSpot !== null}
                <circle cx={cursorX} cy={spotScale.y(atSpot)} r="2.6" class="dot spotd" />
              {/if}
            {/if}
          {/if}

          <!-- ONE shared time axis, labelled once — plus real hour boundaries, because two ticks
               across 6h40m made every intermediate timestamp a guess. -->
          {#each hourTicks as t (t.i)}
            <line x1={xOf(t.i)} x2={xOf(t.i)} y1={H - 14} y2={H - 10} class="tick" />
            <text x={xOf(t.i)} y={H - 2} class="axl" text-anchor="middle">{t.label}</text>
          {/each}
          <text x={PAD_L} y={H - 2} class="axl">{hhmm(sessionDomain!.startMs)}</text>
          <text x={W - PAD_R} y={H - 2} class="axl" text-anchor="middle">{hhmm(sessionDomain!.endMs)}</text>
        </svg>
      </div>
      {#if cursorTMs !== null && cursorX !== null && at === null}
        <p class="muted">{formatTideMinute(cursorTMs)} · ABSENT</p>
      {/if}

    {:else if n === 0 && !loading && !fault}
      <!-- n===0 ONLY. This branch used to fire for n===1 too, so the panel said "one point so far"
           and "returned no points" one line apart — two contradictory claims on screen at once. -->
      <p class="muted">TIDE EMPTY</p>
    {/if}
  </div>
</section>

<style>
  .card {
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow);
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .hd {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    padding: 10px 14px;
    border-bottom: 1px solid var(--line);
  }
  h2 {
    font-size: 12px;
    letter-spacing: 0.12em;
    font-weight: 700;
    color: var(--ink);
    margin: 0;
  }
  .sub { font-size: 11px; color: var(--muted); }
  /* --faint, not --line: on dark, --line on --surface is effectively invisible, so the chip
     announcing "nothing here is calibrated" was the least visible chip on the panel. Matches
     the dealer rail's .hypo exactly. */
  .hyp {
    font-size: 9px;
    letter-spacing: 0.08em;
    padding: 1px 5px;
    border-radius: 4px;
    border: 1px solid var(--faint);
    color: var(--muted);
  }
  /* ORDER 42: `.crossover-toggle` is retired with the CROSSOVERS chip it styled. */
  .display-only { color: var(--faint); font-size: 9px; letter-spacing: 0.06em; }
  .age {
    margin-left: auto;
    font-size: 10px;
    font-family: var(--font-num);
    color: var(--muted);
    padding: 1px 5px;
    border-radius: 3px;
  }
  .age.poll { margin-left: 0; opacity: 0.8; }
  /* The cadence belongs in the reading: '4m 12s' looks alarming until you know the bars are 5m. */
  /* ⚠ --muted, NOT --faint — the SECOND instance of the .ph defect, found by the markup-level
     census and confirmed by GK-i 2026-08-09. This span renders inside `.age.over`, whose
     background is `--stale-soft`; composited over --surface, --faint is 3.98:1 (light) /
     3.76:1 (dark), BELOW AA for this size. --muted is 5.06:1 / 4.69:1.
     ⚠ DARK IS 4.69:1 — only 0.19 above AA. If --muted or --stale-soft is ever re-stepped,
     re-run scripts/validate-palette.mjs; this pair has almost no margin.
     ⇒ Same structural cause as .ph: the COLOUR is set here and the BACKGROUND one level up on
     the parent, so no rule-local search can see the pair. It is a property of the DOM. */
  .cad { color: var(--muted); font-weight: 400; }
  .age.over {
    color: var(--stale-ink);
    background: var(--stale-soft);
    border: 1px solid var(--stale);
    font-weight: 600;
  }
  .age.verdict { letter-spacing: 0.06em; font-weight: 600; }
  /* CLOSED reads as INFORMATION, not alarm — the same treatment StalenessChip gives it, copied
     rather than re-derived. Bordered and weighted so it can never be mistaken for a live chip,
     but never amber: the market being shut is not a fault, and an amber that fires on schedule
     every night is one people learn to ignore. */
  .age.verdict.closed {
    color: var(--muted);
    background: var(--surface-2);
    border: 1px solid var(--line);
  }
  /* A verdict this client does not recognise. DASHED, reusing the token package's own
     "dashed = not-a-thing / cannot vouch for this" grammar (binding rule 1: the flip is dashed,
     magnets solid) — the same vocabulary the desk ratified for NO FEED on the top strip. Full --ink
     because it should be READ, not skimmed past: it is neither health nor fault, it is the server
     saying something true that this panel has not been taught. Never amber — asserting a fault we
     cannot substantiate is the same lie as asserting health. */
  .age.verdict.unknown {
    color: var(--ink);
    background: var(--chip);
    border: 1px dashed var(--line);
  }
  .body { padding: 10px 14px 14px; min-width: 0; }
  .loud, .degrade {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 8px 10px;
    margin-bottom: 8px;
    border-radius: 6px;
    border: 1px solid var(--stale);
    background: var(--stale-soft);
    color: var(--stale-ink);
    font-size: 11px;
  }
  /* --stale-ink, NOT --stale: the palette receipt measured --stale on --stale-soft at 3.14:1,
     which fails AA at this size. --stale stays the border/rule colour. Same rule as
     StalenessChip; copying its treatment rather than re-deriving one. */
  .loud b, .degrade b { color: var(--stale-ink); font-size: 11px; }
  /* The session-split note. Shares the LAYOUT of .loud/.degrade and NONE of their colour: this is
     a statement about the market's shape, not a fault, so it uses the neutral surface. Deliberately
     distinguishable at a glance from the amber blocks directly above it. */
  .split {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 8px 10px;
    margin-bottom: 8px;
    border-radius: 6px;
    border: 1px solid var(--line);
    background: var(--surface-2);
    color: var(--muted);
    font-size: 11px;
  }
  .split b { color: var(--ink); font-size: 11px; }
  .loud button {
    align-self: flex-start;
    margin-top: 4px;
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 4px;
    border: 1px solid var(--line);
    background: var(--surface-2);
    color: var(--ink);
    cursor: pointer;
  }
  .chartwrap { transition: opacity 0.2s ease; overflow-x: auto; }
  /* Dims on ERROR ONLY, and only to 0.7. It used to dim on overBudget too — but this feed runs
     hours behind by design (D-6), so that dim was on 100% of the time. State that is always on
     is not state, and at 0.45 it cost --muted 2.26:1 dark / 1.95:1 light, throwing away more
     contrast than the D-1 palette work bought. Staleness is carried by the worded chip instead. */
  .chartwrap.dimmed { opacity: 0.7; }
  svg { display: block; width: 100%; min-width: 320px; touch-action: pan-y; }

  .zero { stroke: var(--line); stroke-width: 1; }
  .ncp { fill: none; stroke: var(--up); stroke-width: 1.8; }
  .npp { fill: none; stroke: var(--dn); stroke-width: 1.8; }
  /* --vwap, a VALIDATED series colour, not --faint (which tokens.css documents as the AXIS
     LABEL colour). The desk ruled spot in because the divergence read is the panel's point;
     drawing it as chrome contradicted the ruling. */
  .spot { fill: none; stroke: var(--vwap); stroke-width: 1.6; }
  .vol.pos { fill: var(--up); }
  .vol.neg { fill: var(--dn); }

  .axl { font-size: 9px; fill: var(--muted); font-family: var(--font-num); }
  .paneln { font-size: 9px; fill: var(--muted); letter-spacing: 0.4px; }
  .paneln.absent { fill: var(--stale); font-weight: 600; }
  .lbl { font-size: 10px; font-family: var(--font-num); }
  .ncpl { fill: var(--up); }
  .nppl { fill: var(--dn); }

  svg:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; border-radius: 4px; }
  svg.scrubbing { cursor: crosshair; }

  .scrub { stroke: var(--muted); stroke-width: 1; stroke-dasharray: 3 3; pointer-events: none; }
  .cursor-time-chip { fill: var(--muted); font: 9px var(--font-num); pointer-events: none; }
  .dot { pointer-events: none; }
  .dot.ncpd { fill: var(--up); }
  .dot.nppd { fill: var(--dn); }
  .dot.spotd { fill: var(--vwap); }
  .tick { stroke: var(--line); stroke-width: 1; }

  /* Fixed min-height: the readout must not reflow the card as the cursor moves. */
  .readout {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: baseline;
    min-height: 18px;
    margin-top: 6px;
    font-size: 10px;
    font-family: var(--font-num);
    font-variant-numeric: tabular-nums;
  }
  .rt { color: var(--ink); font-weight: 600; }
  .asof { color: var(--faint); }
  .rv { color: var(--muted); }
  .rv.ncpl { color: var(--up); }
  .rv.nppl { color: var(--dn); }
  .rv.spotl { color: var(--vwap); }
  .hint { color: var(--faint); font-family: var(--font-ui); font-size: 10px; }

  /**
   * WORKSPACE PARITY — via `data-density` on <html>, not a prop.
   *
   * The critique's charge was fair: this was the only panel in the shell that did not know Scott
   * exists, rendering identically for both personas. But the fix is NOT a workspace prop. The
   * sibling takes `rows={ws.ladderRows}` because that is DATA (how much chain to render) and CSS
   * cannot express it; density is presentation, and `workspace.ts` deliberately stamps it on the
   * root so panels inherit it "without prop-drilling". Adding a prop here would have fixed the
   * symptom by breaking the pattern.
   *
   * And this panel earns the larger treatment more than any other: `workspace.ts` resolved a
   * three-way source conflict specifically to keep the tide for Scott — "it's Scott's primary read".
   * Scott's workspace is ALSO the one the old permanent dim degraded worst (1.95:1). A primary read
   * rendered at a scanning density was the inversion.
   */
  :global(html[data-density="comfortable"]) .body { padding: 12px 14px 14px; }
  :global(html[data-density="comfortable"]) .readout { font-size: 11px; min-height: 20px; }
  :global(html[data-density="comfortable"]) .foot { font-size: 11px; gap: 14px; }
  :global(html[data-density="comfortable"]) .axl { font-size: 10px; }
  :global(html[data-density="comfortable"]) .paneln { font-size: 10px; }
  :global(html[data-density="comfortable"]) .lbl { font-size: 11px; }
  :global(html[data-density="comfortable"]) .hd { padding: 12px 14px; }

  /**
   * MOBILE (§19 makes the PWA a first-class surface, so this is a gate, not an afterthought).
   *
   * Everything INSIDE the SVG is in viewBox units and shrinks with the card: measured at a 390px
   * viewport the card is 350px wide, so a 560-unit viewBox scales to ~0.58× and a 9px label renders
   * at roughly FIVE pixels — present, and unreadable. The card chrome looked fine throughout because
   * it is CSS px and never scaled, which is exactly why this needed a screenshot at 390px rather
   * than a breakpoint assumption.
   *
   * So SVG type is scaled UP here to land back at ~9-10 effective px, and the volume pane's bars
   * thicken to stay hittable. CSS-px elements (readout, foot, chips) are already correct and are
   * deliberately left alone.
   */
  @media (max-width: 700px) {
    .axl { font-size: 15px; }
    .paneln { font-size: 15px; }
    .lbl { font-size: 16px; }
    .vol { transform: scaleX(1.6); transform-origin: center; }
    .hint { font-size: 10px; }
  }

  .opening {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 10px 12px;
    border: 1px solid var(--line);
    border-radius: 6px;
    background: var(--surface-2);
    font-size: 11px;
  }
  .opening b { color: var(--ink); font-size: 11px; }
  .onerow { display: flex; gap: 12px; flex-wrap: wrap; font-family: var(--font-num); font-variant-numeric: tabular-nums; }

  .foot {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: baseline;
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid var(--line);
    font-size: 11px;
  }
  .foot .muted { font-size: 10px; }
  /* The panel's only synthesised verdict. It was 10px in a flat row while an axis bound nobody
     trades off rendered ~67% larger — the hierarchy was inverted. Pill treatment mirrors the
     dealer rail's .regime so the two cards state their verdicts the same way. */
  .tilt {
    font-family: var(--font-num);
    font-weight: 700;
    letter-spacing: 0.06em;
    font-size: 11px;
    border-radius: 5px;
    padding: 2px 8px;
  }
  .tilt.pos { color: var(--up); background: var(--up-soft); }
  .tilt.neg { color: var(--dn); background: var(--dn-soft); }
  .muted { color: var(--muted); font-size: 11px; }
</style>
